import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Avatar } from "components/avatar";
import type { ServiceAuditSource } from "shared";
import { SourceAvatar } from "./SourceAvatar";
import type { AuditLineSegment } from "./splitAuditLine";

interface Props {
  /**
   * The sentence, pre-split into emphasised / plain runs. Names and changed
   * values come through emphasised; the connective words don't.
   */
  segments: AuditLineSegment[];
  /** Actor name — drives the avatar initials and its hover tooltip. */
  actorName: string;
  actorAvatarUrl?: string;
  /**
   * What performed the action. Anything other than `manual` replaces the person
   * avatar with a glyph — an automation rule has no face, and its
   * `activity_by.name` is null, so the avatar would render empty.
   *
   * Must already be through `parseAuditSource` — `SourceAvatar` indexes total
   * glyph/tooltip maps with it, so a raw wire value lands as an empty circle
   * with no tooltip and no aria-label.
   */
  source?: ServiceAuditSource | null;
  /** Preformatted by `formatAuditTimestamp`. */
  timestamp: string;
  /** Suppresses the connector below the last row so the line doesn't dangle. */
  isLast: boolean;
}

/** Design spec: 24×24, fully round. */
const AVATAR_SIZE = "24px";

/**
 * Breathing room between an avatar and the connector below it.
 *
 * Matches the 4px the drawer puts between rows, which is what separates the
 * connector's lower end from the *next* avatar — so setting the same value here
 * makes the line sit visually centred in the gap rather than growing out of the
 * avatar it hangs from.
 */
const CONNECTOR_GAP = "4px";

/**
 * Drops the sentence so its FIRST LINE centres on the avatar.
 *
 * `body-small` is a 16px line box against a 24px avatar, and the row aligns
 * both to `flex-start` — so without this the text reads 4px high, which is
 * visible once rows stack. (24 − 16) / 2 = 4.
 *
 * Padding rather than `alignItems: center`: centring would work for one line
 * and then push the first line BELOW the avatar on any sentence that wraps.
 * The first line is what the avatar belongs to; the rest flows under it.
 */
const TEXT_OPTICAL_OFFSET = "4px";

export const TimelineRow = ({
  segments,
  actorName,
  actorAvatarUrl,
  timestamp,
  isLast,
  source,
}: Props) => {
  const theme = useTheme();

  // Entries written before the service shipped `source` have no value at all;
  // treat those as manual, per the audit service's own guidance.
  const isMachine = Boolean(source) && source !== "manual";

  return (
    <Stack
      direction="row"
      gap="12px"
      sx={{
        // The list scrolls, and a flex column shrinks fixed-size children once
        // the content overflows — without this the avatars would squash and the
        // row rhythm would collapse as the timeline grows.
        flexShrink: 0,
      }}
    >
      {/* Avatar + connector column */}
      <Stack sx={{ alignItems: "center", flexShrink: 0 }}>
        {isMachine && source ? (
          <SourceAvatar source={source} size={AVATAR_SIZE} />
        ) : (
          <Avatar
            variants="sm"
            fullname={actorName}
            src={actorAvatarUrl}
            tooltip
            // `variants="sm"` is already 24px (Avatar.tsx:61) — only the radius
            // and the shrink guard need saying. The component's own square
            // default would give 4px here.
            sx={{ borderRadius: "48px", flexShrink: 0 }}
          />
        )}
        {!isLast && (
          <Box
            sx={{
              width: "1px",
              // Detaches the line from the avatar's edge. The matching gap at
              // the other end comes from the drawer's 4px row spacing.
              mt: CONNECTOR_GAP,
              // Grows to whatever the sentence needs; `minHeight` keeps a
              // visible segment on single-line rows.
              flex: 1,
              minHeight: "20px",
              backgroundColor: theme.palette.neutral["015"],
            }}
          />
        )}
      </Stack>

      {/* Sentence + timestamp */}
      <Stack
        direction="row"
        gap="12px"
        sx={{
          flex: 1,
          minWidth: 0,
          justifyContent: "space-between",
          alignItems: "flex-start",
          pt: TEXT_OPTICAL_OFFSET,
          // Bottom padding is what separates rows; it sits inside the flex
          // child so the connector spans it rather than breaking across a gap.
          pb: isLast ? 0 : "20px",
        }}
      >
        {/* One Typography, not one per segment — the runs have to reflow as a
            single paragraph, and separate elements would each wrap on their own
            and break the line rhythm. */}
        <Typography
          variant="body-small"
          sx={{ color: theme.palette.lightBg.low, minWidth: 0 }}
        >
          {segments.map((segment, index) => (
            <Box
              component="span"
              // Segments are positional runs of one immutable sentence: the
              // same text can legitimately repeat ("… from Open to Open"), so
              // the index is what disambiguates them. The list never reorders.
              key={`${segment.text}-${index}`}
              sx={{
                color: segment.emphasised
                  ? theme.palette.lightBg.high
                  : "inherit",
              }}
            >
              {segment.text}
            </Box>
          ))}
        </Typography>

        <Typography
          variant="body-xsmall"
          sx={{
            color: theme.palette.lightBg.low,
            flexShrink: 0,
            whiteSpace: "nowrap",
            pt: "2px",
          }}
        >
          {timestamp}
        </Typography>
      </Stack>
    </Stack>
  );
};
